// Evergreen journal templates mirrored from the Worker catalog.
// Used as a Phase-1 fallback when /api/journal-suggestions is unreachable, so Discover
// never looks naked. Personalized ranking still comes from the Worker when available.

import type { JournalSuggestion } from './suggestions';

export const FALLBACK_CATALOG: JournalSuggestion[] = [
  {
    id: 'gratitude_3',
    title: 'Practice gratitude',
    subtitle: 'Name three things from the last day — one person, one moment, one sensation.',
    seedTitle: 'Three gratitudes',
    seedBody:
      "One person I'm grateful for:\n\nOne moment that stayed with me:\n\nOne sensation (light, warmth, quiet, taste…):\n\n",
    chatPrompt:
      "Help me name three things I'm grateful for from the last day — a person, a moment, and a sensation.",
    category: 'practice',
    reason: 'A small practice to land in the page.',
    route: 'composer',
    source: 'catalog',
  },
  {
    id: 'freewrite_5',
    title: 'Freewrite five minutes',
    subtitle: 'No editing. Start from “Right now I notice…” and keep the pen moving.',
    seedTitle: 'Freewrite',
    seedBody: 'Right now I notice…\n\n',
    chatPrompt:
      'I want to freewrite — ask me what I notice right now and keep me going for a few turns.',
    category: 'practice',
    reason: 'A small practice to land in the page.',
    route: 'composer',
    source: 'catalog',
  },
  {
    id: 'body_check',
    title: 'Body check-in',
    subtitle: 'Where are you holding tension? What is it asking for?',
    seedTitle: 'Body check-in',
    seedBody:
      'Where I feel tension:\n\nWhat it might be asking for:\n\nWhat would soften it, even a little:\n\n',
    chatPrompt:
      "Guide me through a gentle body check-in — where I'm holding tension and what it might need.",
    category: 'practice',
    reason: 'A small practice to land in the page.',
    route: 'composer',
    source: 'catalog',
  },
  {
    id: 'manifest_desire',
    title: 'Manifest your deepest desire',
    subtitle: "Write as if it's already true — what do you feel, see, and do tomorrow morning?",
    seedTitle: 'Already true',
    seedBody:
      "Tomorrow morning, living as if it's already true:\n\nWhat I feel in my body:\n\nWhat I see around me:\n\nWhat I do first:\n\n",
    chatPrompt:
      "Help me write from the future — as if my deepest desire is already true. Ask me what I feel, see, and do.",
    category: 'desire',
    reason: "To write from who you're becoming.",
    route: 'composer',
    source: 'catalog',
  },
  {
    id: 'future_letter',
    title: 'Letter from your future self',
    subtitle: 'One year ahead, writing back to today — what do they want you to know?',
    seedTitle: 'From a year ahead',
    seedBody: 'Dear me,\n\n',
    chatPrompt:
      "Help me write a letter from my future self one year from now — what they want today's me to know.",
    category: 'desire',
    reason: "To write from who you're becoming.",
    route: 'composer',
    source: 'catalog',
  },
  {
    id: 'shadow_avoid',
    title: 'What you avoided',
    subtitle: 'The emotion you sidestepped — sit with it on the page without fixing it.',
    seedTitle: 'What I avoided',
    seedBody:
      'The feeling I moved away from:\n\nWhere it showed up:\n\nIf I let it speak for a moment:\n\n',
    chatPrompt:
      'I want to explore an emotion I avoided today. Help me sit with it without rushing to fix it.',
    category: 'inner',
    reason: "A mirror for what's asking attention.",
    route: 'chat',
    source: 'catalog',
  },
  {
    id: 'pattern_thread',
    title: 'Follow a pattern',
    subtitle: 'Pick one recurring loop. Where did it show up this week?',
    seedTitle: 'A pattern I noticed',
    seedBody:
      'The pattern:\n\nWhere it showed up this week:\n\nWhat it might be protecting or asking for:\n\n',
    chatPrompt:
      'Help me trace a recurring pattern from this week — where it showed up and what it might be asking for.',
    category: 'inner',
    reason: "A mirror for what's asking attention.",
    route: 'chat',
    source: 'catalog',
  },
  {
    id: 'dream_aftermath',
    title: 'After the dream',
    subtitle: 'Confrontations, chases, reunions — write the waking truth underneath.',
    seedTitle: 'After the dream',
    seedBody:
      'What happened in the dream:\n\nThe feeling that lingered:\n\nWhat this might be saying about waking life:\n\n',
    chatPrompt:
      'I had a dream that stuck with me. Help me unpack what it might be saying about waking life.',
    category: 'dream',
    reason: "For the night's unfinished sentences.",
    route: 'chat',
    source: 'catalog',
  },
  {
    id: 'dream_symbol',
    title: 'One dream symbol',
    subtitle: 'One image from the night — what would it say if it spoke?',
    seedTitle: 'If this symbol spoke',
    seedBody:
      'The image:\n\nIf it could speak, it would say:\n\nWhat I notice in my body as I write that:\n\n',
    chatPrompt:
      'Help me sit with one image from a recent dream — what it would say if it could speak.',
    category: 'dream',
    reason: "For the night's unfinished sentences.",
    route: 'composer',
    source: 'catalog',
  },
  {
    id: 'hard_conversation',
    title: 'The unsaid conversation',
    subtitle: "Write the talk you're avoiding — then the version you'd actually have.",
    seedTitle: 'The unsaid conversation',
    seedBody:
      "The version I rehearse in my head:\n\nThe version I would actually say:\n\nWhat I'm afraid happens next:\n\n",
    chatPrompt:
      "I'm avoiding a conversation. Help me write what I rehearse in my head, then what I'd actually say.",
    category: 'life',
    reason: 'Something practical to put into words.',
    route: 'chat',
    source: 'catalog',
  },
  {
    id: 'decision_desk',
    title: 'Decision desk',
    subtitle: 'Two paths. Body yes / body no for each — no forcing a winner yet.',
    seedTitle: 'Decision desk',
    seedBody: 'Path A:\nBody yes / no:\n\nPath B:\nBody yes / no:\n\nWhat I notice:\n\n',
    chatPrompt:
      'Help me weigh a decision with a body yes/no for each path — without forcing a winner yet.',
    category: 'life',
    reason: 'Something practical to put into words.',
    route: 'composer',
    source: 'catalog',
  },
  {
    id: 'release_list',
    title: 'Ready to leave',
    subtitle: 'What can leave this week — a habit, a story, a grip?',
    seedTitle: 'Ready to leave',
    seedBody:
      "What I'm ready to release:\n\nWhat keeping it costs me:\n\nOne small act of letting go:\n\n",
    chatPrompt:
      "Help me name something I'm ready to release this week, and one small act of letting go.",
    category: 'life',
    reason: 'Something practical to put into words.',
    route: 'composer',
    source: 'catalog',
  },
  {
    id: 'one_kindness',
    title: 'One kindness',
    subtitle: 'The kindest thing you did — or withheld — today.',
    seedTitle: 'One kindness',
    seedBody:
      'The kindest thing I offered (or withheld):\n\nWho it was for:\n\nWhat that says about what I needed:\n\n',
    chatPrompt:
      'Help me reflect on one kindness I offered or withheld today, and what I needed underneath.',
    category: 'practice',
    reason: 'A small practice to land in the page.',
    route: 'composer',
    source: 'catalog',
  },
  {
    id: 'alignment_scan',
    title: 'Alignment scan',
    subtitle: "Where did you act like the person you're becoming? Where not?",
    seedTitle: 'Alignment scan',
    seedBody:
      "Moments I acted like who I'm becoming:\n\nMoments I drifted:\n\nOne adjustment for tomorrow:\n\n",
    chatPrompt:
      "Help me scan today for moments I acted like who I'm becoming — and where I drifted.",
    category: 'desire',
    reason: "To write from who you're becoming.",
    route: 'composer',
    source: 'catalog',
  },
];

/** Stable day rotation so Suggested for you isn't empty offline. */
export function fallbackTodaySuggestions(count = 4): JournalSuggestion[] {
  const day = Math.floor(Date.now() / 86_400_000);
  const out: JournalSuggestion[] = [];
  for (let i = 0; i < count && i < FALLBACK_CATALOG.length; i++) {
    out.push(FALLBACK_CATALOG[(day + i * 3) % FALLBACK_CATALOG.length]);
  }
  return out;
}
